import React, { useEffect, useRef, useState } from 'react';
import { fabric } from 'fabric';
import styled from 'styled-components';
import useEditorStore from '../store/useEditorStore';
import { FaVectorSquare, FaEye, FaExternalLinkAlt, FaCopy } from 'react-icons/fa';
import ObjectControls from './ShortcodeWidget';
import { attachEmojiShortcuts } from '../utils/emojiShortcuts';
import { applyStrokeSettings } from '../utils/strokeUtils';
// PerspectiveOverlay removed — perspective is handled via corner controls + server-side rendering

const IMAGE_SERVER_URL = import.meta.env.VITE_IMAGE_SERVER_URL || 'http://localhost:5002';
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';
const TEXT_OBJECT_TYPES = ['i-text', 'textbox'] as const;
const MIN_TEXTBOX_WIDTH = 60;
const MIN_TEXTBOX_HEIGHT = 24;
const MIN_FONT_SIZE = 10;
const MAX_FONT_SIZE = 400;
const PLACEHOLDER_TEXT = 'Enter text here';

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const isTextObject = (obj: fabric.Object | null | undefined): obj is fabric.IText => {
  if (!obj) return false;
  return TEXT_OBJECT_TYPES.includes(obj.type as (typeof TEXT_OBJECT_TYPES)[number]);
};

const ArtboardContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.background};
  overflow: hidden;
`;

const ArtboardWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;

  &.dragging {
    border: 3px dashed #4285f4;
    background: rgba(66, 133, 244, 0.05);
  }

  .canvas-container {
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
    border-radius: 8px;
    position: absolute !important;
    left: 50% !important;
    top: 50% !important;
    transform: translate(-50%, -50%) !important;
    max-width: 100% !important;
    max-height: 100% !important;
  }

  .canvas-container canvas {
    width: 100% !important;
    height: 100% !important;
    object-fit: contain;
  }

  .lower-canvas, .upper-canvas {
    width: 100% !important;
    height: 100% !important;
  }
`;

const DropOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(66, 133, 244, 0.08);
  border: 3px dashed #4285f4;
  border-radius: 8px;
  z-index: 100;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease;
  backdrop-filter: blur(1px);

  &.active {
    opacity: 1;
    animation: pulse 1.5s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% {
      border-color: #4285f4;
      background: rgba(66, 133, 244, 0.08);
      box-shadow: 0 0 0 0 rgba(66, 133, 244, 0.4);
    }
    50% {
      border-color: #5c9eff;
      background: rgba(66, 133, 244, 0.12);
      box-shadow: 0 0 20px 5px rgba(66, 133, 244, 0.3);
    }
  }
`;

const DropMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  color: #4285f4;
  text-align: center;
  pointer-events: none;
`;

const DropIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(66, 133, 244, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  border: 2px solid rgba(66, 133, 244, 0.3);
  animation: bounce 1s ease-in-out infinite;

  @keyframes bounce {
    0%, 100% {
      transform: translateY(0) scale(1);
    }
    50% {
      transform: translateY(-12px) scale(1.05);
    }
  }
`;

const DropText = styled.div`
  font-size: 28px;
  font-weight: 600;
  color: #4285f4;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const DropSubtext = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: #666;
  margin-top: 8px;
`;

const ArtboardLabel = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 13px;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const ArtboardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #4285f4;
  border-radius: 4px;
  color: white;
`;

const DimensionInput = styled.input`
  width: 60px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  color: #000000;
  padding: 4px 8px;
  font-size: 13px;
  text-align: center;

  &:focus {
    outline: none;
    border-color: #4285f4;
  }

  .dark & {
    background: #374151;
    border-color: #4b5563;
    color: #e5e7eb;
  }

  /* Hide number input spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type=number] {
    -moz-appearance: textfield;
  }
`;

const Separator = styled.span`
  color: #000000;
  margin: 0 4px;

  .dark & {
    color: #e5e7eb;
  }
`;

const CopiedMessage = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #28a745;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1001;
  margin-top: 4px;
  animation: fadeInOut 2s ease-in-out;
  
  @keyframes fadeInOut {
    0% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
    20% { opacity: 1; transform: translateX(-50%) translateY(0); }
    80% { opacity: 1; transform: translateX(-50%) translateY(0); }
    100% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
  }
`;

const ColorButton = styled.button`
  
`;

const ColorSwatch = styled.div<{ $color: string }>`
  width: 100%;
  height: 100%;
  background-color: ${props => props.$color};
  // border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 3px;
`;

const ColorPickerInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const PreviewButton = styled.button`
  
`;

const PreviewModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const PreviewContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewImageContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  max-height: 100%;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 90vh;
  height: auto;
  display: block;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(0, 0, 0, 0.7);
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
  z-index: 10;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }
`;

const ViewImageButton = styled.button`
  position: absolute;
  bottom: 16px;
  right: 16px;
  background: rgba(66, 133, 244, 0.9);
  border: none;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  z-index: 10;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);

  &:hover {
    background: rgba(66, 133, 244, 1);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const Preloader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: #fff;
  border-radius: 8px;
`;

const Spinner = styled.div`
  width: 15px;
  height: 15px;
  border: 1px solid rgba(255,255,255,0.3);
  border-top: 1px solid #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #333;
`;

interface ArtboardProps {
  width: number;
  height: number;
  backgroundColor?: string;
  backgroundImage?: string;
}

const Artboard: React.FC<ArtboardProps> = ({
  width,
  height,
  backgroundColor: _backgroundColor = '#ffffff',
  backgroundImage
}) => {
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const isGeneratingRef = useRef(false); // Add ref to track generation state
  const historyLockRef = useRef(false);
  const conversionLockRef = useRef(false);
  const [canvasReady, setCanvasReady] = useState(false);
  const {
    canvas,
    setCanvas,
    addToHistory,
    selectedTool,
    zoom,
    setSelectedObject,
    setArtboardWidth,
    setArtboardHeight,
    setArtboardBackgroundColor,
    artboardBackgroundColor,
    selectedObject,
    setSelectedTool,
    shapeSettings
  } = useEditorStore();

  const [localWidth, setLocalWidth] = useState(width);
  const [localHeight, setLocalHeight] = useState(height);

  // Update local dimensions when props change (new artboard or editing different artboard)
  useEffect(() => {
    setLocalWidth(width);
    setLocalHeight(height);
  }, [width, height]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const [showHubSpotCopiedMessage, setShowHubSpotCopiedMessage] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isObjectsLoading, setIsObjectsLoading] = useState(() => {
    const artboardId = window.location.pathname.split('/').pop();
    return artboardId !== 'editor' && !!artboardId;
  });

  // Listen for objectsLoaded to dismiss artboard loader
  useEffect(() => {
    const handleObjectsLoaded = () => setIsObjectsLoading(false);
    window.addEventListener('objectsLoaded', handleObjectsLoaded);
    return () => window.removeEventListener('objectsLoaded', handleObjectsLoaded);
  }, []);

  // Suppress font loading warnings in development
  useEffect(() => {
    const originalWarn = console.warn;
    console.warn = (...args) => {
      // Suppress font-related warnings
      const message = args.join(' ');
      if (message.includes('Failed to decode downloaded font') || 
          message.includes('OTS parsing error')) {
        return;
      }
      originalWarn.apply(console, args);
    };

    return () => {
      console.warn = originalWarn;
    };
  }, []);

  // Effect to reset loading state when previewImage changes
  useEffect(() => {
    if (previewImage && !isGeneratingPreview) {
      // Reset loading state when a new image URL is set
      setIsImageLoading(true);
      setImageLoadError(false);
    }
  }, [previewImage, isGeneratingPreview]);

  // Function to ensure consistent corner styles for all objects
  const ensureConsistentCornerStyles = (obj: fabric.Object) => {
    obj.set({
      cornerStyle: 'circle',
      cornerSize: 8,
      transparentCorners: false,
      cornerColor: '#4285f4',
      cornerStrokeColor: '#ffffff',
      borderColor: '#4285f4',
      borderScaleFactor: 1,
      padding: 8
    });
  };

const applyPlaceholderBehavior = (text: fabric.IText) => {
  const placeholder = PLACEHOLDER_TEXT;
  const placeholderColor = (text as any).placeholderColor || 'rgba(0, 0, 0, 0.3)';

  if ((text as any)._placeholderSetup) {
    return;
  }

  (text as any)._placeholderSetup = true;
  (text as any).placeholder = placeholder;
  (text as any).placeholderColor = placeholderColor;

  text.on('editing:entered', () => {
    if (text.text === placeholder) {
      text.set('text', '');
    }
  });

  text.on('editing:exited', () => {
    if (!text.text || text.text.trim() === '') {
      text.set('text', placeholder);
    }
  });

  const originalRender = text.render;
  text.render = function(ctx: CanvasRenderingContext2D) {
    originalRender.call(this, ctx);
    if (!this.text) {
      if (this.isEditing) {
        return;
      }
      ctx.save();
      ctx.fillStyle = placeholderColor;
      ctx.font = `${this.fontSize}px ${this.fontFamily}`;
      ctx.fillText(
        placeholder,
        (this.left || 0) + (this.padding || 0),
        (this.top || 0) + (this.fontSize || 20)
      );
      ctx.restore();
    }
  };
};

const configureTextObject = (
  textObj: fabric.IText,
  options: { enablePlaceholder?: boolean } = {}
) => {
  textObj.set({
    hoverCursor: 'text',
    selectable: true,
    hasControls: true,
    hasBorders: true,
    lockScalingFlip: true,
    lockUniScaling: false,
    lockScalingX: false,
    lockScalingY: false,
    lockRotation: false,
    padding: textObj.padding ?? 8,
    editable: true,
    splitByGrapheme: false
  } as any);

  ensureConsistentCornerStyles(textObj);
  attachEmojiShortcuts(textObj);

  if (options.enablePlaceholder) {
    applyPlaceholderBehavior(textObj);
  }
};

const enforceTextboxConstraints = (
  textbox: fabric.Textbox,
  targetWidth: number,
  targetHeight: number
) => {
  const canvasWidth = textbox.canvas?.width ?? targetWidth;
  const canvasHeight = textbox.canvas?.height ?? targetHeight;
  const width = clamp(targetWidth, MIN_TEXTBOX_WIDTH, canvasWidth);
  const height = clamp(targetHeight, MIN_TEXTBOX_HEIGHT, canvasHeight);

  textbox.set({
    width,
    height,
    dirty: true
  });

  textbox.initDimensions();

  let currentHeight = textbox.height || 0;
  if (currentHeight > height) {
    let fontSize = textbox.fontSize || 20;
    const ratio = height / currentHeight;
    fontSize = clamp(Math.floor(fontSize * ratio), MIN_FONT_SIZE, MAX_FONT_SIZE);
    textbox.set('fontSize', fontSize);
    textbox.initDimensions();
    currentHeight = textbox.height || 0;
    while (currentHeight > height && fontSize > MIN_FONT_SIZE) {
      fontSize -= 1;
      textbox.set('fontSize', fontSize);
      textbox.initDimensions();
      currentHeight = textbox.height || 0;
    }
  } else if (height > currentHeight) {
    textbox.set('height', height);
  }
};

// Helper function to set id from name if name exists
const setIdFromName = (obj: fabric.Object) => {
  const objAny = obj as any;
  if (objAny.name && !objAny.id) {
    objAny.id = objAny.name;
  }
};

const convertITextToTextbox = (
  textObj: fabric.IText,
  canvasInstance: fabric.Canvas,
  enablePlaceholder = false
): fabric.Textbox | null => {
  try {
    const serialized = textObj.toObject([
      'placeholder',
      'placeholderColor',
      'hoverCursor'
    ]) as Record<string, any>;
    const { text = '', type: _type, version: _version, ...rest } = serialized;

    const textbox = new fabric.Textbox(text || '', {
      ...(rest as fabric.ITextboxOptions),
      width: rest.width || textObj.getScaledWidth() || 240,
      splitByGrapheme: false,
      lockUniScaling: false,
      lockScalingFlip: true
    });

    configureTextObject(textbox, { enablePlaceholder });

    // Set id from name if name exists (preserve id from original object)
    setIdFromName(textbox);
    // Also preserve id if original object had one
    const originalId = (textObj as any).id;
    if (originalId) {
      (textbox as any).id = originalId;
    }

    const index = canvasInstance.getObjects().indexOf(textObj);
    canvasInstance.insertAt(
      textbox,
      index >= 0 ? index : canvasInstance.getObjects().length,
      false
    );
    canvasInstance.remove(textObj);

    return textbox;
  } catch (error) {
    console.error('Failed to convert text object:', error);
    return null;
  }
};

  // Function to update shortcodes in canvas objects
  const updateShortcodes = (params: URLSearchParams) => {
    if (!canvas) return;

    const objects = canvas.getObjects();
    let hasChanges = false;

    objects.forEach(obj => {
      if (isTextObject(obj)) {
        const textObj = obj as fabric.IText;
        let text = textObj.text || '';
        const shortcodes = [
          { key: 'firstName', value: '{{firstName}}' },
          { key: 'FirstName', value: '{{FirstName}}' },
          { key: 'lastName', value: '{{lastName}}' },
          { key: 'LastName', value: '{{LastName}}' },
          { key: 'email', value: '{{email}}' },
          { key: 'Email', value: '{{Email}}' },
          { key: 'companyName', value: '{{companyName}}' },
          { key: 'CompanyName', value: '{{CompanyName}}' },
          { key: 'Country', value: '{{Country}}' },
          { key: 'Title', value: '{{Title}}' },
          { key: 'date', value: '{{date}}' },
          { key: 'Date', value: '{{Date}}' },
          { key: 'time', value: '{{time}}' },
          { key: 'Time', value: '{{Time}}' }
        ];

        // Check if the text contains any shortcodes
        const containsShortcodes = shortcodes.some(({ value }) => text.includes(value));
        if (containsShortcodes) {
          // Check if ALL shortcodes in the text should be hidden
          const shouldHideObject = shortcodes.every(({ key, value }) => {
            if (text.includes(value)) {
              // If this shortcode is in the text, check if it should be hidden
              if (!params.has(key)) {
                return true; // Hide if parameter doesn't exist
              }
              const paramValue = params.get(key);
              // Hide if parameter is empty, blank, or contains the shortcode pattern itself
              return !paramValue || paramValue.trim() === '' || paramValue === value;
            }
            return true; // If shortcode not in text, consider it "hidden" (doesn't affect visibility)
          });

          if (shouldHideObject) {
            // Hide the entire object if all shortcodes in the text should be hidden
            textObj.set('visible', false);
            hasChanges = true;
            return;
          } else {
            // Show the object if at least one shortcode has a valid parameter
            textObj.set('visible', true);
            
            // Process shortcode replacements for objects that should be visible
            shortcodes.forEach(({ key, value }) => {
              if (params.has(key) && text.includes(value)) {
                const paramValue = params.get(key);
                // Only replace if the parameter has a non-empty value and is not the shortcode pattern itself
                if (paramValue && paramValue.trim() !== '' && paramValue !== value) {
                  const newText = text.replace(new RegExp(value, 'g'), paramValue);
                  if (newText !== text) {
                    text = newText;
                    hasChanges = true;
                  }
                }
              }
            });
          }
        } else {
          // If text doesn't contain shortcodes but is empty or only whitespace, hide it
          if (!text || text.trim() === '') {
            textObj.set('visible', false);
            hasChanges = true;
            return;
          }
        }

        // Additional check: if text is very short after processing, hide it
        if (text && text.trim().length <= 3) {
          textObj.set('visible', false);
          hasChanges = true;
          return;
        }

        if (text !== textObj.text) {
          // Store original properties
          const originalFontSize = textObj.fontSize || 20;
          const originalLeft = textObj.left || 0;
          const originalTop = textObj.top || 0;
          const originalTextAlign = textObj.textAlign || 'left';

          // Calculate available space
          const canvasWidth = canvas.width!;
          const canvasHeight = canvas.height!;
          const padding = 20;

          // First try with original font size
          textObj.set({
            text: text,
            width: canvasWidth - (padding * 2),
            textAlign: originalTextAlign,
            fontSize: originalFontSize,
            left: originalLeft,
            top: originalTop
          });

          // Check if text overflows
          const textWidth = textObj.width || 0;
          const maxWidth = canvasWidth - (padding * 2);
          const maxHeight = canvasHeight - (padding * 2);

          // If text is too wide, reduce font size
          if (textWidth > maxWidth) {
            const scaleFactor = maxWidth / textWidth;
            const newFontSize = Math.floor(originalFontSize * scaleFactor);
            
            textObj.set({
              fontSize: Math.max(newFontSize, 12),
              width: maxWidth
            });
          }

          // If text is too tall after wrapping, reduce font size further
          const finalHeight = textObj.height || 0;
          if (finalHeight > maxHeight) {
            const heightScaleFactor = maxHeight / finalHeight;
            const currentFontSize = textObj.fontSize || originalFontSize;
            const newFontSize = Math.floor(currentFontSize * heightScaleFactor);
            
            textObj.set({
              fontSize: Math.max(newFontSize, 12)
            });
          }

          // Ensure text stays within bounds while maintaining original position
          const finalWidth = textObj.width || 0;
          const finalLeft = Math.min(
            Math.max(padding, originalLeft),
            canvasWidth - finalWidth - padding
          );
          const finalTop = Math.min(
            Math.max(padding, originalTop),
            canvasHeight - (textObj.height || 0) - padding
          );

          textObj.set({
            left: finalLeft,
            top: finalTop
          });
        }
      }
    });

    if (hasChanges) {
      // Preserve the current zoom level
      const currentZoom = canvas.getZoom();
      
      // Update the canvas
      canvas.renderAll();
      
      // Restore zoom level
      canvas.setZoom(currentZoom);
      
      // Ensure background color is preserved
      canvas.setBackgroundColor(artboardBackgroundColor, () => {
        canvas.renderAll();
      });
    }
  };

  // Effect to handle URL parameters (shortcodes and image generation)
  useEffect(() => {
    if (!canvas) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const imageParam = params.get('image');
    const shouldGenerateImage = imageParam === 'true';
    const hasShortcodes = Array.from(params.keys()).some(key => 
      ['firstName', 'FirstName', 'lastName', 'LastName', 'email', 'Email', 'companyName', 'CompanyName', 'Country', 'Title', 'date', 'Date', 'time', 'Time'].includes(key)
    );

    // Update shortcodes if they exist
    if (hasShortcodes) {
      updateShortcodes(params);
    }

    // Generate image if requested
    if (shouldGenerateImage && !isGeneratingRef.current) {
      isGeneratingRef.current = true;

      // Small delay to ensure shortcodes are updated
      setTimeout(() => {
        generateImage().then(dataUrl => {
          if (dataUrl) {
            // Create a temporary link and trigger download
            const link = document.createElement('a');
            link.href = dataUrl;
            link.download = `artboard-${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Remove the image parameter from URL to prevent re-triggering
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('image');
            window.history.replaceState({}, '', newUrl.toString());
          } else {
            console.error('Failed to generate image: empty data URL');
          }
          isGeneratingRef.current = false;
        });
      }, 1000); // Increased delay to ensure shortcodes are updated
    }
  }, [canvas, window.location.search]); // Remove updateShortcodes from dependencies since it's defined in the component

  // Function to generate image from canvas
  const generateImage = async () => {
    if (!canvas) {
      console.error('No canvas available for image generation');
      return null;
    }
    
    // Store current zoom level
    const currentZoom = canvas.getZoom();

    // Reset zoom to 1 for accurate image generation
    canvas.setZoom(1);

    // Ensure all objects are visible and properly rendered
    const objects = canvas.getObjects();
    objects.forEach(obj => {
      obj.set({
        selectable: false,
        hasControls: false,
        hasBorders: false
      });
    });

    // Force a complete render
    canvas.renderAll();

    // Generate image data URL with a slight delay to ensure rendering is complete
    return new Promise<string>((resolve) => {
      setTimeout(() => {
        try {
          // Use Fabric.js's toDataURL method with consistent scaling
          const dataUrl = canvas.toDataURL({
            format: 'png',
            quality: 1,
            multiplier: 1, // Use 1 to match editor view
            enableRetinaScaling: false
          });
          
          // Restore zoom level
          canvas.setZoom(currentZoom);
          
          // Restore object properties
          objects.forEach(obj => {
            obj.set({
              selectable: true,
              hasControls: true,
              hasBorders: true
            });
          });
          
          canvas.renderAll();
          
          resolve(dataUrl);
        } catch (error) {
          console.error('Error generating image:', error);
          // Restore zoom level even on error
          canvas.setZoom(currentZoom);
          canvas.renderAll();
          resolve('');
        }
      }, 1000); // Increased delay to ensure rendering is complete
    });
  };

  // Function to generate preview with demo data
  const generatePreview = async () => {
    if (!canvas) {
      console.error('No canvas available for preview generation');
      return;
    }

    setIsGeneratingPreview(true);
    setIsImageLoading(true);
    
    try {
      // Get the current artboard ID from the URL path
      const pathSegments = window.location.pathname.split('/');
      const artboardId = pathSegments[pathSegments.length - 1];
      
      // If we're on a new artboard (no ID), show an error
      if (artboardId === 'editor') {
        alert('Please save the artboard first before generating a preview.');
        setIsGeneratingPreview(false);
        setIsImageLoading(false);
        return;
      }
      
      // Demo data for preview
      const demoData = {
        firstName: 'John',
        lastName: 'Doe',
        email: 'john@example.com',
        companyName: 'Demo Company',
        Country: 'United States',
        Title: 'Software Developer',
        date: '2024-01-15',
        time: '14:30'
      };

      // Create the preview URL with demo data
      const previewUrl = `${IMAGE_SERVER_URL}/public-image/${artboardId}?${new URLSearchParams(demoData).toString()}`;
      
      // Set the preview image URL and show modal
      setPreviewImage(previewUrl);
      setShowPreview(true);
      
      // Reset generation state but keep image loading state
      setIsGeneratingPreview(false);
      
      // Add a timeout to prevent infinite loading
      setTimeout(() => {
        if (isImageLoading) {
          setIsImageLoading(false);
          setImageLoadError(true);
        }
      }, 10000); // 10 second timeout
      
    } catch (error) {
      console.error('Error generating preview:', error);
      alert('Error generating preview. Please try again.');
      setIsGeneratingPreview(false);
      setIsImageLoading(false);
    }
  };

  // Function to close preview modal
  const closePreview = () => {
    setShowPreview(false);
    setPreviewImage('');
    setIsImageLoading(false);
    setImageLoadError(false);
  };

  // Function to copy the generatable image code with shortcodes
  const copyImageUrl = async () => {
    if (!canvas) {
      console.error('No canvas available for URL generation');
      return;
    }
    
    if (!canvasReady) {
      console.error('Canvas not ready yet');
      return;
    }

    try {
      // Get the current artboard ID from the URL path
      const pathSegments = window.location.pathname.split('/');
      const artboardId = pathSegments[pathSegments.length - 1];
      
      // If we're on a new artboard (no ID), show an error
      if (artboardId === 'editor') {
        alert('Please save the artboard first before copying the image code.');
        return;
      }

      // Scan the artboard for used shortcodes
      const usedShortcodes = new Set<string>();
      const allShortcodes = ['firstName', 'lastName', 'email', 'companyName', 'Country', 'Title', 'date', 'time'];
      
      // Only add shortcodes that are actually found in the canvas text
      
      if (canvas && canvas.getObjects) {
        const objects = canvas.getObjects();

        objects.forEach((obj) => {
          if (isTextObject(obj)) {
            const textObj = obj as fabric.IText;
            const text = textObj.text || '';

            allShortcodes.forEach(shortcode => {
              if (text.includes(`{{${shortcode}}}`)) {
                usedShortcodes.add(shortcode);
              }
            });
          }
        });
      }

      // Create the base URL
      const baseUrl = `${IMAGE_SERVER_URL}/public-image/${artboardId}`;
      
      // Build URL with only the shortcodes that are actually used
      let urlWithShortcodes = baseUrl;
      if (usedShortcodes.size > 0) {
        const shortcodeParams = Array.from(usedShortcodes)
          .map(shortcode => `${shortcode}={{${shortcode}}}`)
          .join('&');
        urlWithShortcodes = `${baseUrl}?${shortcodeParams}`;
      }
      
      // Generate HTML image code
      const imageCode = `<img src="${urlWithShortcodes}" alt="Dynamic Image" style="max-width: 100%; height: auto;" />`;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(imageCode);
      
      // Show copied message
      setShowCopiedMessage(true);
      setTimeout(() => {
        setShowCopiedMessage(false);
      }, 2000); // Hide after 2 seconds
      
    } catch (error) {
      console.error('Error copying image code:', error);
      // alert('Error copying image code. Please try again.');
    }
  };

  // Function to copy HubSpot compatible image code
  const copyHubSpotUrl = async () => {
    if (!canvas) {
      console.error('No canvas available for URL generation');
      return;
    }
    
    if (!canvasReady) {
      console.error('Canvas not ready yet');
      return;
    }

    try {
      // Get the current artboard ID from the URL path
      const pathSegments = window.location.pathname.split('/');
      const artboardId = pathSegments[pathSegments.length - 1];
      
      // If we're on a new artboard (no ID), show an error
      if (artboardId === 'editor') {
        alert('Please save the artboard first before copying the image code.');
        return;
      }

      // Scan the artboard for used shortcodes
      const usedShortcodes = new Set<string>();
      const allShortcodes = ['firstName', 'lastName', 'email', 'companyName', 'Country', 'Title', 'date', 'time'];
      
      if (canvas && canvas.getObjects) {
        const objects = canvas.getObjects();

        objects.forEach(obj => {
          if (isTextObject(obj)) {
            const textObj = obj as fabric.IText;
            const text = textObj.text || '';

            allShortcodes.forEach(shortcode => {
              if (text.includes(`{{${shortcode}}}`)) {
                usedShortcodes.add(shortcode);
              }
            });
          }
        });
      }

      // Create the base URL
      const baseUrl = `${IMAGE_SERVER_URL}/public-image/${artboardId}`;
      
      // Build URL with HubSpot merge field format
      let urlWithHubSpotFields = baseUrl;
      if (usedShortcodes.size > 0) {
        // Map shortcodes to HubSpot merge fields
        const hubspotMapping: { [key: string]: string } = {
          'firstName': '{{contact.firstname}}',
          'lastName': '{{contact.lastname}}',
          'email': '{{contact.email}}',
          'companyName': '{{contact.company}}',
          'Country': '{{contact.country}}',
          'Title': '{{contact.jobtitle}}',
          'date': '{{contact.date}}',
          'time': '{{contact.time}}'
        };
        
        // Manually construct the URL to avoid URL encoding of {{}} characters
        const hubspotParams = Array.from(usedShortcodes)
          .map(shortcode => `${shortcode}=${hubspotMapping[shortcode] || `{{${shortcode}}}`}`)
          .join('&');
        urlWithHubSpotFields = `${baseUrl}?${hubspotParams}`;
      }
      
      // Generate HTML image code
      const imageCode = `<img src="${urlWithHubSpotFields}" alt="Dynamic Image" style="max-width: 100%; height: auto;" />`;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(imageCode);
      
      // Show copied message
      setShowHubSpotCopiedMessage(true);
      setTimeout(() => {
        setShowHubSpotCopiedMessage(false);
      }, 2000); // Hide after 2 seconds
      
    } catch (error) {
      console.error('Error copying HubSpot image code:', error);
      // alert('Error copying HubSpot image code. Please try again.');
    }
  };

  // Initialize canvas
  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) {
      return;
    }

    // Add a small delay to ensure DOM is ready
    const initTimeout = setTimeout(() => {
      try {
        const container = containerRef.current;
        if (!container) {
          return;
        }

        const containerWidth = container.getBoundingClientRect().width;
        const containerHeight = container.getBoundingClientRect().height;

        // Calculate scale to fit the artboard within the container while maintaining aspect ratio
        // and respecting the maximum dimensions
        const maxWidth = Math.min(width, containerWidth * 0.9);
        const maxHeight = Math.min(height, containerHeight * 0.9);
        const scaleX = maxWidth / width;
        const scaleY = maxHeight / height;
        const scale = Math.min(scaleX, scaleY);

        // Ensure the canvas element is ready
        const canvasElement = canvasRef.current;
        if (!canvasElement) {
          console.error('Canvas element not found');
          return;
        }

        // Create canvas with specified dimensions
        const fabricCanvas = new fabric.Canvas(canvasElement, {
          width: width,
          height: height,
          backgroundColor: artboardBackgroundColor,
          preserveObjectStacking: true,
        });

        // Declare handlers outside setTimeout so cleanup can access them
        let handleTextScaling: ((event: fabric.IEvent) => void) | null = null;
        let handleObjectAdded: ((e: fabric.IEvent) => void) | null = null;
        let historyHandler: (() => void) | null = null;

        // Wait for canvas to be fully initialized
        setTimeout(() => {
          try {
            // Scale container size - canvas elements will scale via CSS (width: 100%)
            // But Fabric.js zoom stays at 1, so objects maintain their pixel coordinates
            const canvasContainer = (fabricCanvas as any).wrapperEl as HTMLElement;
            if (canvasContainer) {
              // Set container to scaled size
              const scaledWidth = width * scale;
              const scaledHeight = height * scale;
              canvasContainer.style.width = `${scaledWidth}px`;
              canvasContainer.style.height = `${scaledHeight}px`;
              // Remove any transform
              canvasContainer.style.transform = '';
              canvasContainer.style.transformOrigin = '';
            }
            
            // CRITICAL: Keep zoom at 1 so objects maintain their actual pixel dimensions
            // CSS will handle visual scaling of canvas elements, but Fabric.js coordinates stay the same
            fabricCanvas.setZoom(1);
            
            // Recalculate canvas offset for proper object positioning
            fabricCanvas.calcOffset();
            fabricCanvas.renderAll();

            handleTextScaling = (event: fabric.IEvent) => {
              let target = event.target;
              if (!target || !isTextObject(target)) return;

              if (target.type === 'i-text' && !conversionLockRef.current) {
                conversionLockRef.current = true;
                historyLockRef.current = true;
                const converted = convertITextToTextbox(target as fabric.IText, fabricCanvas);
                historyLockRef.current = false;
                conversionLockRef.current = false;
                if (converted) {
                  target = converted;
                  setSelectedObject(converted);
                }
              }

              if (!isTextObject(target)) {
                return;
              }

              const textbox = target as fabric.Textbox;
              const scaleX = textbox.scaleX || 1;
              const scaleY = textbox.scaleY || 1;
              const baseWidth = textbox.width || textbox.getScaledWidth() || MIN_TEXTBOX_WIDTH;
              const baseHeight = textbox.height || textbox.getScaledHeight() || MIN_TEXTBOX_HEIGHT;
              const newWidth = Math.max(MIN_TEXTBOX_WIDTH, baseWidth * scaleX);
              const newHeight = Math.max(MIN_TEXTBOX_HEIGHT, baseHeight * scaleY);

              textbox.set({
                scaleX: 1,
                scaleY: 1
              });

              enforceTextboxConstraints(textbox, newWidth, newHeight);
              textbox.setCoords();
              fabricCanvas.requestRenderAll();
            };

            if (handleTextScaling) {
              fabricCanvas.on('object:scaling', handleTextScaling);
            }

            handleObjectAdded = (e: fabric.IEvent) => {
              const obj = e.target;
              if (!obj) return;

              let targetObj = obj;

              if (isTextObject(targetObj) && targetObj.type === 'i-text' && !conversionLockRef.current) {
                conversionLockRef.current = true;
                historyLockRef.current = true;
                const converted = convertITextToTextbox(targetObj as fabric.IText, fabricCanvas);
                historyLockRef.current = false;
                conversionLockRef.current = false;
                if (converted) {
                  targetObj = converted;
                }
              }

              if (isTextObject(targetObj)) {
                configureTextObject(targetObj, { enablePlaceholder: false });
              }

              // Set id from name if name exists
              setIdFromName(targetObj);

              if (selectedTool === 'select') {
                obj.set({
                  hoverCursor: 'move'
                });
                fabricCanvas.renderAll();
              }
            };

            if (handleObjectAdded) {
              fabricCanvas.on('object:added', handleObjectAdded);
            }

            setCanvas(fabricCanvas);
            
            // Set up event listeners
            historyHandler = () => {
              if (historyLockRef.current) return;
              addToHistory();
            };

            if (historyHandler) {
              fabricCanvas.on('object:modified', historyHandler);
              fabricCanvas.on('object:added', historyHandler);
              fabricCanvas.on('object:removed', historyHandler);
            }
            
            // Wait a bit more to ensure canvas is fully functional
            setTimeout(() => {
              // Additional check to ensure canvas is truly ready
              if (fabricCanvas && 
                  typeof fabricCanvas.getContext === 'function' && 
                  typeof fabricCanvas.getObjects === 'function' &&
                  typeof fabricCanvas.renderAll === 'function' &&
                  typeof fabricCanvas.loadFromJSON === 'function') {
                setCanvasReady(true); // Set canvas ready
              } else {
                console.warn('Artboard: Canvas not fully functional yet, waiting...');
                // Try again after a longer delay
                setTimeout(() => {
                  setCanvasReady(true);
                }, 500);
              }
            }, 200);

          } catch (error) {
            console.error('Error in canvas post-initialization:', error);
          }
        }, 100);

        // Handle window resize
        const handleResize = () => {
          const containerWidth = container.getBoundingClientRect().width;
          const containerHeight = container.getBoundingClientRect().height;
          
          // Calculate scale while maintaining aspect ratio and respecting max dimensions
          const maxWidth = Math.min(width, containerWidth * 0.9);
          const maxHeight = Math.min(height, containerHeight * 0.9);
          const scaleX = maxWidth / width;
          const scaleY = maxHeight / height;
          const scale = Math.min(scaleX, scaleY);
          
          // Scale container size - canvas elements will scale via CSS (width: 100%)
          // But Fabric.js zoom stays at 1, so objects maintain their pixel coordinates
          const canvasContainer = (fabricCanvas as any).wrapperEl as HTMLElement;
          if (canvasContainer) {
            // Set container to scaled size
            const scaledWidth = width * scale;
            const scaledHeight = height * scale;
            canvasContainer.style.width = `${scaledWidth}px`;
            canvasContainer.style.height = `${scaledHeight}px`;
            // Remove any transform
            canvasContainer.style.transform = '';
            canvasContainer.style.transformOrigin = '';
          }
          
          // CRITICAL: Keep zoom at 1 so objects maintain their actual pixel dimensions
          // CSS will handle visual scaling of canvas elements, but Fabric.js coordinates stay the same
          fabricCanvas.setZoom(1);
          
          // Recalculate canvas offset for proper coordinate mapping
          fabricCanvas.calcOffset();
          fabricCanvas.renderAll();
        };

        window.addEventListener('resize', handleResize);

        return () => {
          if (handleTextScaling) {
            fabricCanvas.off('object:scaling', handleTextScaling);
          }
          if (handleObjectAdded) {
            fabricCanvas.off('object:added', handleObjectAdded);
          }
          if (historyHandler) {
            fabricCanvas.off('object:modified', historyHandler);
            fabricCanvas.off('object:added', historyHandler);
            fabricCanvas.off('object:removed', historyHandler);
          }
          fabricCanvas.dispose();
          window.removeEventListener('resize', handleResize);
        };
      } catch (error) {
        console.error('Error initializing canvas:', error);
      }
    }, 50);

    return () => {
      clearTimeout(initTimeout);
    };
  }, []); // Only run once on mount

  // Notify parent when canvas is ready
  useEffect(() => {
    if (canvasReady) {
      // Dispatch custom event to notify that canvas is ready
      window.dispatchEvent(new CustomEvent('canvasReady', { 
        detail: { ready: true } 
      }));
      
      // For new artboards, also dispatch objects loaded event since there are no objects to load
      const artboardId = window.location.pathname.split('/').pop();
      const isNewArtboard = artboardId === 'editor' || !artboardId;
      if (isNewArtboard) {
        window.dispatchEvent(new CustomEvent('objectsLoaded', {
          detail: { ready: true }
        }));
      }
    }
  }, [canvasReady]);

  // Handle dimension and background color changes
  useEffect(() => {
    if (!canvas || !containerRef.current) {
      return;
    }

    // Add a small delay to ensure canvas is ready
    const updateTimeout = setTimeout(() => {
      try {
        const container = containerRef.current;
        if (!container) return;

        const containerWidth = container.getBoundingClientRect().width;
        const containerHeight = container.getBoundingClientRect().height;

        // Additional safety check
        if (!canvas.setDimensions || typeof canvas.setDimensions !== 'function') {
          console.error('Canvas setDimensions method not available');
          return;
        }

        // Check if canvas context is available
        if (!canvas.getContext()) {
          console.error('Canvas context not available');
          return;
        }

        // Store current canvas state (strip backgroundImage - handled separately via useEffect)
        const json = canvas.toJSON();
        delete (json as any).backgroundImage;
        const oldWidth = canvas.width || width;
        const oldHeight = canvas.height || height;
        
        // Calculate scale factor for objects when canvas dimensions change
        const widthScale = width / oldWidth;
        const heightScale = height / oldHeight;

        // Update canvas dimensions and background
        canvas.setDimensions({ width, height });
        canvas.setBackgroundColor(artboardBackgroundColor, () => {
          // Calculate scale while maintaining aspect ratio and respecting max dimensions
          const maxWidth = Math.min(width, containerWidth * 0.9);
          const maxHeight = Math.min(height, containerHeight * 0.9);
          const scaleX = maxWidth / width;
          const scaleY = maxHeight / height;
          const scale = Math.min(scaleX, scaleY);

          // Restore objects with proportional scaling if dimensions changed
          canvas.loadFromJSON(json, () => {
            // Only scale objects down when artboard shrinks, never scale up
            const isShrinking = widthScale < 1 || heightScale < 1;
            if (isShrinking) {
              const uniformScale = Math.min(widthScale, heightScale);
              const objects = canvas.getObjects();
              objects.forEach(obj => {
                // Reposition proportionally
                const newLeft = (obj.left || 0) * widthScale;
                const newTop = (obj.top || 0) * heightScale;

                // Scale down using scaleX/scaleY (correct for all object types including images)
                const currentScaleX = obj.scaleX || 1;
                const currentScaleY = obj.scaleY || 1;

                obj.set({
                  left: newLeft,
                  top: newTop,
                  scaleX: currentScaleX * uniformScale,
                  scaleY: currentScaleY * uniformScale,
                });

                obj.setCoords();
              });
            }
            
            // Scale container size - canvas elements will scale via CSS (width: 100%)
            // But Fabric.js zoom stays at 1, so objects maintain their pixel coordinates
            const canvasContainer = (canvas as any).wrapperEl as HTMLElement;
            if (canvasContainer) {
              // Set container to scaled size
              const scaledWidth = width * scale;
              const scaledHeight = height * scale;
              canvasContainer.style.width = `${scaledWidth}px`;
              canvasContainer.style.height = `${scaledHeight}px`;
              // Remove any transform
              canvasContainer.style.transform = '';
              canvasContainer.style.transformOrigin = '';
            }
            
            // CRITICAL: Keep zoom at 1 so objects maintain their actual pixel dimensions
            // CSS will handle visual scaling of canvas elements, but Fabric.js coordinates stay the same
            canvas.setZoom(1);
            
            // Recalculate canvas offset for proper object positioning
            canvas.calcOffset();
            canvas.renderAll();
          });
        });
      } catch (error) {
        console.error('Error updating canvas dimensions:', error);
      }
    }, 100);

    return () => {
      clearTimeout(updateTimeout);
    };
  }, [width, height, artboardBackgroundColor, canvas]);

  // Update the loadArtboardData effect to handle background color properly
  useEffect(() => {
    const loadArtboardData = async () => {
      if (!canvas) return; // Early return if canvas is not initialized

      // Add a delay to ensure canvas is fully ready
      setTimeout(() => {
        try {
          // Check if canvas context is available
          if (!canvas.getContext()) {
            console.error('Canvas context not available for loading data');
            return;
          }

          const artboardId = window.location.pathname.split('/').pop();
          const isNewArtboard = artboardId === 'editor' || !artboardId;
          if (isNewArtboard) {
            // For new artboards, dispatch objects loaded event immediately
            window.dispatchEvent(new CustomEvent('objectsLoaded', {
              detail: { ready: true }
            }));
            return;
          }

          // Only proceed if we have a valid artboardId
          if (!artboardId) {
            window.dispatchEvent(new CustomEvent('objectsLoaded', {
              detail: { ready: true }
            }));
            return;
          }

          const token = localStorage.getItem('token');
          if (!token) return;

          fetch(`${API_URL}/api/artboards/${artboardId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
          .then(response => {
            if (!response.ok) {
              throw new Error('Failed to fetch artboard data');
            }
            return response.json();
          })
          .then(data => {
            if (data.data) {
              // Strip backgroundImage from JSON data before loading -
              // it's saved as { src: "..." } which is not valid Fabric.js image serialization.
              // The useEffect watching the backgroundImage prop handles it correctly via fabric.Image.fromURL.
              const jsonData = { ...data.data };
              delete jsonData.backgroundImage;

              // Load the canvas data first
              canvas.loadFromJSON(jsonData, async () => {
                
                // First pass: Handle SVG groups that need to be reloaded
                const objects = canvas.getObjects();
                const svgPromises: Promise<void>[] = [];
                
                for (let i = 0; i < objects.length; i++) {
                  const obj = objects[i];
                  if (obj.type === 'group' && (obj as any).svgData) {
                    const svgData = (obj as any).svgData;
                    const objProps = {
                      left: obj.left,
                      top: obj.top,
                      scaleX: obj.scaleX,
                      scaleY: obj.scaleY,
                      angle: obj.angle,
                      skewX: obj.skewX,
                      skewY: obj.skewY,
                      flipX: obj.flipX,
                      flipY: obj.flipY,
                      opacity: obj.opacity
                    };
                    
                    svgPromises.push(new Promise<void>((resolve) => {
                      fabric.loadSVGFromString(svgData, (svgObjects, options) => {
                        if (svgObjects && svgObjects.length > 0) {
                          const svgGroup = fabric.util.groupSVGElements(svgObjects, options);
                          svgGroup.set({
                            ...objProps,
                            selectable: true,
                            hasControls: true,
                            hasBorders: true,
                            evented: true,
                            cornerStyle: 'circle',
                            cornerSize: 8,
                            transparentCorners: false,
                            cornerColor: '#4285f4',
                            cornerStrokeColor: '#ffffff',
                            borderColor: '#4285f4',
                            borderScaleFactor: 1,
                            padding: 8
                          });
                          (svgGroup as any).svgData = svgData;
                          
                          // Set id from name if name exists
                          setIdFromName(svgGroup);
                          
                          // Replace the old group with the properly loaded SVG
                          canvas.remove(obj);
                          canvas.insertAt(svgGroup, i, false);
                        }
                        resolve();
                      });
                    }));
                  }
                }
                
                // Wait for all SVGs to be reloaded
                if (svgPromises.length > 0) {
                  await Promise.all(svgPromises);
                }
                
                // Refresh the objects list after SVG replacements
                const updatedObjects = canvas.getObjects();
                
                updatedObjects.forEach(obj => {
                  obj.set({
                    selectable: true,
                    hasControls: true,
                    hasBorders: true,
                    evented: true,
                    cornerStyle: 'circle',
                    cornerSize: 8,
                    transparentCorners: false,
                    cornerColor: '#4285f4',
                    cornerStrokeColor: '#ffffff',
                    borderColor: '#4285f4',
                    borderScaleFactor: 1,
                    padding: 8
                  });
                  
                  // Set id from name if name exists
                  setIdFromName(obj);
                  
                  // Perspective data is preserved via perspectiveEnabled/perspectiveCorners properties
                  // (serialized in propertiesToInclude). Visual rendering happens via PerspectiveOverlay
                  // when the perspective tool is active, and via server-side rendering for generated images.

                  // Special handling for text objects
                  if (isTextObject(obj)) {
                    const textObj = obj as fabric.IText;
                    (textObj as any).set({
                      hoverCursor: 'text',
                      editingBorderColor: '#4285f4',
                      cursorColor: '#ffffff',
                      cursorWidth: 2,
                      cursorDuration: 500,
                      selectionColor: 'rgba(66, 133, 244, 0.3)',
                      selectionBackgroundColor: 'rgba(66, 133, 244, 0.1)',
                    });
                  }
                  
                  // Apply outside stroke configuration for shapes with strokes
                  const shapeTypes = ['rect', 'rectangle', 'circle', 'ellipse', 'triangle', 'polygon', 'path'];
                  if (shapeTypes.includes(obj.type || '')) {
                    const stroke = obj.stroke as string;
                    const rawStrokeWidth = (obj.strokeWidth as number) || 0;
                    if (stroke && rawStrokeWidth > 0) {
                      // If previously saved with outside stroke (paintFirst: 'stroke'),
                      // the stored width is doubled — halve it to get the logical width
                      const logicalWidth = obj.paintFirst === 'stroke' ? rawStrokeWidth / 2 : rawStrokeWidth;
                      applyStrokeSettings(obj, stroke, logicalWidth);
                    }
                  }
                });
                
                // Re-apply background image after loadFromJSON (it was stripped to avoid Fabric deserialization issues)
                const bgImageData = data.data.backgroundImage;
                if (bgImageData) {
                  const bgSrc = typeof bgImageData === 'string' ? bgImageData : bgImageData.src;
                  if (bgSrc) {
                    fabric.Image.fromURL(bgSrc, (img) => {
                      if (!img || !img.width) return;
                      const imgW = img.width || 1;
                      const imgH = img.height || 1;
                      const bgScale = Math.max(width / imgW, height / imgH);
                      img.set({
                        originX: 'center',
                        originY: 'center',
                        left: width / 2,
                        top: height / 2,
                        scaleX: bgScale,
                        scaleY: bgScale,
                        selectable: false,
                        evented: false,
                      });
                      canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas));
                    }, { crossOrigin: 'anonymous' });
                  }
                }

                // Then set the background color from the saved data
                if (data.data.backgroundColor) {
                  setArtboardBackgroundColor(data.data.backgroundColor);
                  canvas.setBackgroundColor(data.data.backgroundColor, () => {
                    canvas.renderAll();

                    // Wait for all rendering to complete before dispatching objects loaded event
                    setTimeout(() => {
                      // Force a final render to ensure everything is displayed
                      canvas.renderAll();
                      
                      // Verify that objects are actually loaded and rendered
                      const finalObjects = canvas.getObjects();

                      if (finalObjects.length > 0) {
                        // Dispatch objects loaded event after all objects are processed and rendered
                        window.dispatchEvent(new CustomEvent('objectsLoaded', {
                          detail: { ready: true, objectCount: finalObjects.length }
                        }));
                      } else {
                        window.dispatchEvent(new CustomEvent('objectsLoaded', {
                          detail: { ready: true, objectCount: 0 }
                        }));
                      }
                    }, 500); // Wait 500ms for all rendering to complete
                  });
                } else {
                  // If no background color, still wait for rendering
                  setTimeout(() => {
                    // Force a final render to ensure everything is displayed
                    canvas.renderAll();
                    
                    // Verify that objects are actually loaded and rendered
                    const finalObjects = canvas.getObjects();

                    if (finalObjects.length > 0) {
                      // Dispatch objects loaded event after all objects are processed and rendered
                      window.dispatchEvent(new CustomEvent('objectsLoaded', {
                        detail: { ready: true, objectCount: finalObjects.length }
                      }));
                    } else {
                      window.dispatchEvent(new CustomEvent('objectsLoaded', {
                        detail: { ready: true, objectCount: 0 }
                      }));
                    }
                  }, 500); // Wait 500ms for all rendering to complete
                }
              });
            }
          })
          .catch(error => {
            console.error('Error loading artboard data:', error);
            // Still dispatch event even on error to prevent infinite loading
            window.dispatchEvent(new CustomEvent('objectsLoaded', { 
              detail: { ready: true } 
            }));
          });
        } catch (error) {
          console.error('Error in loadArtboardData:', error);
          // Still dispatch event even on error to prevent infinite loading
          window.dispatchEvent(new CustomEvent('objectsLoaded', { 
            detail: { ready: true } 
          }));
        }
      }, 200); // Wait for canvas to be fully initialized
    };

    loadArtboardData();
  }, [canvas]);

  // Add a new effect to handle background color changes only when explicitly changed by user
  useEffect(() => {
    if (!canvas) return;
    
    // Only update background color if it's different from the current canvas background
    const currentBgColor = canvas.backgroundColor as string;
    if (currentBgColor !== artboardBackgroundColor) {
      
      // Temporarily disable selection and controls
      const objects = canvas.getObjects();
      objects.forEach(obj => {
        obj.set({
          selectable: false,
          hasControls: false,
          hasBorders: false
        });
      });

      // Set the new background color
      canvas.setBackgroundColor(artboardBackgroundColor, () => {
        // Re-enable selection and controls
        objects.forEach(obj => {
          obj.set({
            selectable: true,
            hasControls: true,
            hasBorders: true,
            cornerStyle: 'circle',
            cornerSize: 8,
            transparentCorners: false,
            cornerColor: '#4285f4',
            cornerStrokeColor: '#ffffff',
            borderColor: '#4285f4',
            borderScaleFactor: 1,
            padding: 8
          });
        });
        
        // Ensure proper rendering
        canvas.renderAll();
      });
    }
  }, [artboardBackgroundColor, canvas]);

  const handleWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value > 0) {
      setLocalWidth(value);
    }
  };

  const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value > 0) {
      setLocalHeight(value);
    }
  };

  const handleWidthBlur = () => {
    if (localWidth !== width) {
      setArtboardWidth(localWidth);
    }
  };

  const handleHeightBlur = () => {
    if (localHeight !== height) {
      setArtboardHeight(localHeight);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    
    // Update the state
    setArtboardBackgroundColor(newColor);
    
    // Directly update the canvas background color
    if (canvas) {
      canvas.setBackgroundColor(newColor, () => {
        canvas.renderAll();
      });
    }
  };

  const handleImageUpload = (file: File) => {
    const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');

    // SVG: load as vector and scale to fit the artboard (fixes tiny/invisible SVGs)
    if (isSvg) {
      const svgReader = new FileReader();
      svgReader.onload = (event) => {
        const svgText = event.target?.result as string;

        fabric.loadSVGFromString(svgText, (objects, options) => {
          if (!canvas) return;
          const svgObj = fabric.util.groupSVGElements(objects, options);

          // Compute scale to fit within 80% of the artboard
          const canvasWidth = canvas.width!;
          const canvasHeight = canvas.height!;
          const maxWidth = canvasWidth * 0.8;
          const maxHeight = canvasHeight * 0.8;
          const scaleX = maxWidth / (svgObj.width || 1);
          const scaleY = maxHeight / (svgObj.height || 1);
          const scale = Math.min(scaleX, scaleY, 1); // don't upscale beyond 1x

          svgObj.set({
            left: canvasWidth / 2,
            top: canvasHeight / 2,
            originX: 'center',
            originY: 'center',
            scaleX: scale,
            scaleY: scale,
            selectable: true,
            hasControls: true,
            hasBorders: true,
            cornerStyle: 'circle',
            cornerSize: 8,
            transparentCorners: false,
            cornerColor: '#4285f4',
            cornerStrokeColor: '#ffffff',
            borderColor: '#4285f4',
            borderScaleFactor: 1,
            padding: 8,
            opacity: 1
          });

          // Store the raw SVG for future edits
          (svgObj as any).svgData = svgText;

          // Set id from name if name exists
          setIdFromName(svgObj);

          canvas.add(svgObj);
          canvas.setActiveObject(svgObj);
          ensureConsistentCornerStyles(svgObj);

          canvas.requestRenderAll();
          setSelectedTool('select');
        });
      };
      svgReader.readAsText(file);
      return;
    }

    // Non-SVG images: convert to PNG for compatibility
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;

      const tempImg = new Image();
      tempImg.onerror = () => {
        console.error('Failed to load image. Format may not be supported:', file.type);
        alert('Failed to load image. This format may not be fully supported. Please try PNG, JPG, or WebP.');
      };
      tempImg.onload = () => {
        // Create a temporary canvas to convert image to PNG
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = tempImg.width;
        tempCanvas.height = tempImg.height;
        const ctx = tempCanvas.getContext('2d');
        
        if (!ctx) {
          console.error('Could not get canvas context');
          return;
        }
        
        // Draw the image to the temporary canvas
        ctx.drawImage(tempImg, 0, 0);
        
        // Convert to PNG for compatibility
        const pngDataUrl = tempCanvas.toDataURL('image/png');
        
        fabric.Image.fromURL(pngDataUrl, (img) => {
          if (!canvas) return;

          // Get the actual image dimensions
          const imgWidth = img.width!;
          const imgHeight = img.height!;
          
          // Get artboard dimensions
          const canvasWidth = canvas.width!;
          const canvasHeight = canvas.height!;
          
          // Calculate scaling only if image is larger than artboard
          let scale = 1;
          if (imgWidth > canvasWidth || imgHeight > canvasHeight) {
            const scaleX = canvasWidth / imgWidth;
            const scaleY = canvasHeight / imgHeight;
            scale = Math.min(scaleX, scaleY);
          }
          
          // Set image properties
          img.set({
            left: canvasWidth / 2,
            top: canvasHeight / 2,
            originX: 'center',
            originY: 'center',
            scaleX: scale,
            scaleY: scale,
            selectable: true,
            hasControls: true,
            hasBorders: true,
            cornerStyle: 'circle',
            cornerSize: 8,
            transparentCorners: false,
            cornerColor: '#4285f4',
            cornerStrokeColor: '#ffffff',
            borderColor: '#4285f4',
            borderScaleFactor: 1,
            padding: 8
          });

          // Store PNG image data
          (img as any).src = pngDataUrl;
          (img as any).dataUrl = pngDataUrl;
          
          // Set id from name if name exists
          setIdFromName(img);
          
          // Add the image to canvas
          canvas.add(img);
          canvas.setActiveObject(img);
          ensureConsistentCornerStyles(img);
          
          // Ensure background color is preserved
          canvas.setBackgroundColor(artboardBackgroundColor, () => {
            canvas.renderAll();
          });
          
          // Switch back to select tool after adding the image
          setSelectedTool('select');
        });
      };
      tempImg.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  // Handle background image changes
  useEffect(() => {
    if (!canvas) return;
    if (!backgroundImage) {
      canvas.setBackgroundImage(null as any, canvas.renderAll.bind(canvas));
      return;
    }
    
    fabric.Image.fromURL(backgroundImage, (img) => {
      if (!img || !img.width) {
        console.error('Failed to load background image');
        return;
      }
      const imgW = img.width || 1;
      const imgH = img.height || 1;
      const scale = Math.max(width / imgW, height / imgH);
      img.set({
        originX: 'center',
        originY: 'center',
        left: width / 2,
        top: height / 2,
        scaleX: scale,
        scaleY: scale,
        selectable: false,
        evented: false,
      });
      canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas));
    }, { crossOrigin: 'anonymous' });
  }, [backgroundImage, canvas, width, height]);

  // Handle zoom changes (only if not using CSS transform for responsive scaling)
  useEffect(() => {
    if (!canvas) return;
    // Only apply zoom if it's a user-initiated zoom change, not responsive scaling
    // Responsive scaling uses CSS transform instead of Fabric.js zoom
    const canvasContainer = (canvas as any).wrapperEl as HTMLElement;
    if (canvasContainer && canvasContainer.style.transform && canvasContainer.style.transform.includes('scale')) {
      // If CSS transform is being used for responsive scaling, don't apply zoom
      return;
    }
    canvas.setZoom(zoom);
    canvas.renderAll();
  }, [zoom, canvas]);

  // Handle tool selection
  useEffect(() => {
    if (!canvas) return;

    // Remove existing event listeners
    canvas.off('mouse:down');

    switch (selectedTool) {
      case 'text':
        canvas.isDrawingMode = false;
        canvas.selection = true; // Enable selection so existing text objects can be selected
        canvas.defaultCursor = 'text';
        
        // Set cursor for all objects to text cursor
        canvas.getObjects().forEach(obj => {
          if (isTextObject(obj)) {
            obj.set({
              hoverCursor: 'text',
              selectable: true,
              hasControls: true,
              hasBorders: true
            });
          } else {
            obj.set({
              hoverCursor: 'default'
            });
          }
        });

        // Add text functionality
        const handleTextClick = async (options: any) => {
          // Get the pointer position
          const pointer = canvas.getPointer(options.e);
          
          // Check if we clicked on an existing object
          const target = canvas.findTarget(options.e, false);
          if (target) {
            // If we clicked on an existing text object, just select it
            if (isTextObject(target)) {
              const textTarget = target as fabric.IText;
              canvas.setActiveObject(textTarget);
              setSelectedObject(textTarget);
              ensureConsistentCornerStyles(textTarget);
              canvas.renderAll();
              return;
            }
            // If we clicked on a non-text object, still create new text on top
            // This allows adding text on top of images and other objects
          }
          
          // Ensure the font is loaded before creating the text object
          const fontFamily = 'Inter';
          const fontWeight = 400;
          
          if ('fonts' in document) {
            try {
              await document.fonts.load(`${fontWeight} 16px ${fontFamily}`);
            } catch (error) {
              console.warn(`Failed to load font for new text: ${fontWeight} ${fontFamily}`, error);
            }
          }
          
          // Create a new text object
          const text = new fabric.Textbox('', {
            left: pointer.x,
            top: pointer.y,
            fontFamily: fontFamily,
            fontSize: 20,
            fontWeight: fontWeight,
            fill: '#000000',
            width: 240,
            textAlign: 'left',
            selectable: true,
            hasControls: true,
            hasBorders: true,
            lockScalingX: false,
            lockScalingY: false,
            lockRotation: false,
            cornerStyle: 'circle',
            cornerSize: 8,
            transparentCorners: false,
            cornerColor: '#4285f4',
            cornerStrokeColor: '#ffffff',
            borderColor: '#4285f4',
            borderScaleFactor: 1,
            padding: 8,
            hasRotatingPoint: true,
            rotatingPointOffset: 40,
            evented: true,
            hoverCursor: 'text',
            editingBorderColor: '#4285f4',
            cursorColor: '#ffffff',
            cursorWidth: 2,
            cursorDuration: 500,
            selectionColor: 'rgba(66, 133, 244, 0.3)',
            selectionBackgroundColor: 'rgba(66, 133, 244, 0.1)',
            charSpacing: 0,
            lineHeight: 1.16,
            noScaleCache: false,
            splitByGrapheme: true
          });
          configureTextObject(text, { enablePlaceholder: true });

          // Set id from name if name exists
          setIdFromName(text);

          // Add the text object to canvas and bring it to front
          canvas.add(text);
          canvas.bringToFront(text);
          canvas.setActiveObject(text);
          setSelectedObject(text);
          ensureConsistentCornerStyles(text);
          text.enterEditing();
          
          // Force a render to ensure proper layering
          canvas.requestRenderAll();
          
          // Ensure the text is visible and properly positioned
          setTimeout(() => {
            if (canvas && text) {
              // Double-check that text is at the front
              canvas.bringToFront(text);
              canvas.requestRenderAll();
              
            }
          }, 100);
        };

        canvas.on('mouse:down', handleTextClick);
        
        // Add double-click handler for editing existing text objects
        const handleDoubleClick = (options: any) => {
          const target = canvas.findTarget(options.e, false);
          if (target && isTextObject(target)) {
            const textObj = target as fabric.IText;
            canvas.setActiveObject(textObj);
            setSelectedObject(textObj);
            ensureConsistentCornerStyles(textObj);
            textObj.enterEditing();
            canvas.renderAll();
          }
        };
        
        canvas.on('mouse:dblclick', handleDoubleClick);
        
        return () => {
          canvas.off('mouse:down');
          canvas.off('mouse:dblclick');
        };
        break;

      case 'image':
        canvas.isDrawingMode = false;
        canvas.selection = true;
        canvas.defaultCursor = 'default';
        // Create and trigger file input
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => {
          const file = (e.target as HTMLInputElement).files?.[0];
          if (file) {
            handleImageUpload(file);
          }
        };
        input.click();
        break;

      case 'rectangle':
      case 'circle':
      case 'line':
        canvas.isDrawingMode = false;
        canvas.selection = true; // Enable selection so existing objects can be moved
        canvas.defaultCursor = 'crosshair';
        
        let isDrawing = false;
        let startPoint: { x: number; y: number } | null = null;
        let currentShape: fabric.Object | null = null;

        const handleShapeMouseDown = (options: any) => {
          // Check if user clicked on an existing object
          const target = canvas.findTarget(options.e, false);
          if (target) {
            // If clicked on existing object, allow normal selection/movement
            // Don't create a new shape
            return;
          }
          
          const pointer = canvas.getPointer(options.e);
          isDrawing = true;
          startPoint = pointer;
          
          const shapeType = selectedTool;
          let shape: fabric.Object;

          if (shapeType === 'rectangle') {
            shape = new fabric.Rect({
              left: pointer.x,
              top: pointer.y,
              width: 0,
              height: 0,
              fill: shapeSettings.fillEnabled ? shapeSettings.fillColor : 'transparent',
              stroke: shapeSettings.strokeEnabled ? shapeSettings.strokeColor : '',
              strokeWidth: shapeSettings.strokeEnabled ? shapeSettings.strokeWidth : 0,
              selectable: false,
              evented: false,
            });
            if (shapeSettings.strokeEnabled && shapeSettings.strokeColor && shapeSettings.strokeWidth > 0) {
              applyStrokeSettings(shape, shapeSettings.strokeColor, shapeSettings.strokeWidth);
            }
          } else if (shapeType === 'circle') {
            shape = new fabric.Circle({
              left: pointer.x,
              top: pointer.y,
              radius: 0,
              fill: shapeSettings.fillEnabled ? shapeSettings.fillColor : 'transparent',
              stroke: shapeSettings.strokeEnabled ? shapeSettings.strokeColor : '',
              strokeWidth: shapeSettings.strokeEnabled ? shapeSettings.strokeWidth : 0,
              selectable: false,
              evented: false,
            });
            if (shapeSettings.strokeEnabled && shapeSettings.strokeColor && shapeSettings.strokeWidth > 0) {
              applyStrokeSettings(shape, shapeSettings.strokeColor, shapeSettings.strokeWidth);
            }
          } else { // line
            shape = new fabric.Line([pointer.x, pointer.y, pointer.x, pointer.y], {
              stroke: shapeSettings.strokeEnabled ? shapeSettings.strokeColor : '#000000',
              strokeWidth: shapeSettings.strokeEnabled ? shapeSettings.strokeWidth : 2,
              selectable: false,
              evented: false,
            });
            // Lines don't need outside stroke rendering as they don't have fills
          }

          currentShape = shape;
          canvas.add(shape);
        };

        const handleShapeMouseMove = (options: any) => {
          if (!isDrawing || !startPoint || !currentShape) return;
          
          const pointer = canvas.getPointer(options.e);
          const shapeType = selectedTool;

          if (shapeType === 'rectangle') {
            const rect = currentShape as fabric.Rect;
            const width = Math.abs(pointer.x - startPoint.x);
            const height = Math.abs(pointer.y - startPoint.y);
            rect.set({
              left: Math.min(startPoint.x, pointer.x),
              top: Math.min(startPoint.y, pointer.y),
              width,
              height,
            });
          } else if (shapeType === 'circle') {
            const circle = currentShape as fabric.Circle;
            const radius = Math.sqrt(
              Math.pow(pointer.x - startPoint.x, 2) + Math.pow(pointer.y - startPoint.y, 2)
            ) / 2;
            circle.set({
              left: startPoint.x - radius,
              top: startPoint.y - radius,
              radius,
            });
          } else { // line
            const line = currentShape as fabric.Line;
            line.set({
              x1: startPoint.x,
              y1: startPoint.y,
              x2: pointer.x,
              y2: pointer.y,
            });
          }

          canvas.renderAll();
        };

        const handleShapeMouseUp = () => {
          if (!isDrawing || !currentShape) return;
          
          isDrawing = false;
          
          // Configure the shape for selection and editing
          currentShape.set({
            selectable: true,
            hasControls: true,
            hasBorders: true,
            evented: true,
          });
          
          ensureConsistentCornerStyles(currentShape);
          canvas.setActiveObject(currentShape);
          setSelectedObject(currentShape);
          canvas.renderAll();
          
          startPoint = null;
          currentShape = null;
        };

        canvas.on('mouse:down', handleShapeMouseDown);
        canvas.on('mouse:move', handleShapeMouseMove);
        canvas.on('mouse:up', handleShapeMouseUp);

        return () => {
          canvas.off('mouse:down');
          canvas.off('mouse:move');
          canvas.off('mouse:up');
        };

      case 'perspective':
        canvas.isDrawingMode = false;
        canvas.selection = true;
        canvas.defaultCursor = 'crosshair';

        // Compute affine matrix from perspective corners and apply as Fabric properties
        // Uses tl, tr, bl to determine the 6 affine params (br is derived as a parallelogram)
        const computeAndApplyPerspective = (obj: fabric.Object, cornerKey?: string) => {
          const corners = (obj as any).perspectiveCorners;
          if (!corners) return;

          const w = obj.width || 1;
          const h = obj.height || 1;

          // When dragging br, adjust bl to maintain a valid parallelogram
          if (cornerKey === 'br') {
            corners.bl = {
              x: corners.br.x - corners.tr.x + corners.tl.x,
              y: corners.br.y - corners.tr.y + corners.tl.y,
            };
          }

          // Compute affine matrix from tl, tr, bl
          // Maps local coords (-w/2,-h/2)..(w/2,h/2) to canvas coords
          const a = (corners.tr.x - corners.tl.x) / w;
          const b = (corners.tr.y - corners.tl.y) / w;
          const c = (corners.bl.x - corners.tl.x) / h;
          const d = (corners.bl.y - corners.tl.y) / h;
          const ex = corners.tl.x + (w / 2) * a + (h / 2) * c;
          const fy = corners.tl.y + (w / 2) * b + (h / 2) * d;

          // Decompose matrix [a,b,c,d,e,f] to Fabric properties
          const angle = Math.atan2(b, a) * 180 / Math.PI;
          const sx = Math.sqrt(a * a + b * b);
          const det = a * d - b * c;
          const sy = det / sx;
          const tanKx = (a * c + b * d) / (a * a + b * b);
          const kx = Math.atan(tanKx) * 180 / Math.PI;

          obj.set({
            angle: angle,
            scaleX: sx,
            scaleY: Math.abs(sy),
            skewX: kx,
            skewY: 0,
            flipY: sy < 0,
          });
          obj.setPositionByOrigin(new fabric.Point(ex, fy), 'center', 'center');

          // Sync br to the affine-determined position (parallelogram constraint)
          corners.br = {
            x: corners.tr.x + corners.bl.x - corners.tl.x,
            y: corners.tr.y + corners.bl.y - corners.tl.y,
          };

          obj.setCoords();
        };

        const createPerspectiveControl = (cornerKey: 'tl' | 'tr' | 'bl' | 'br') => {
          const posMap: Record<string, [number, number]> = {
            tl: [-0.5, -0.5], tr: [0.5, -0.5], bl: [-0.5, 0.5], br: [0.5, 0.5]
          };
          return new fabric.Control({
            x: posMap[cornerKey][0],
            y: posMap[cornerKey][1],
            cursorStyle: 'crosshair',
            actionName: 'perspective',
            actionHandler: (_eventData: any, transform: any) => {
              const target = transform.target;
              const canvasInst = target.canvas;
              if (!canvasInst) return false;

              const pointer = canvasInst.getPointer(_eventData.e);
              const corners = (target as any).perspectiveCorners;
              if (!corners) return false;

              corners[cornerKey] = { x: pointer.x, y: pointer.y };
              (target as any).perspectiveEnabled = true;

              computeAndApplyPerspective(target, cornerKey);
              canvasInst.requestRenderAll();
              return true;
            },
            render: (ctx: CanvasRenderingContext2D, left: number, top: number) => {
              ctx.save();
              ctx.fillStyle = '#4285f4';
              ctx.strokeStyle = '#ffffff';
              ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.arc(left, top, 8, 0, Math.PI * 2);
              ctx.fill();
              ctx.stroke();
              ctx.restore();
            },
          });
        };

        const applyPerspectiveControls = (obj: fabric.Object) => {
          if (!obj) return;

          // Initialize perspective corners from object's current bounding coords
          if (!(obj as any).perspectiveCorners) {
            obj.setCoords();
            const coords = obj.aCoords || (obj as any).oCoords;
            if (coords) {
              (obj as any).perspectiveCorners = {
                tl: { x: coords.tl.x, y: coords.tl.y },
                tr: { x: coords.tr.x, y: coords.tr.y },
                bl: { x: coords.bl.x, y: coords.bl.y },
                br: { x: coords.br.x, y: coords.br.y },
              };
            } else {
              const w = (obj.width || 0) * (obj.scaleX || 1);
              const h = (obj.height || 0) * (obj.scaleY || 1);
              const l = obj.left || 0;
              const t = obj.top || 0;
              (obj as any).perspectiveCorners = {
                tl: { x: l, y: t },
                tr: { x: l + w, y: t },
                bl: { x: l, y: t + h },
                br: { x: l + w, y: t + h },
              };
            }
            (obj as any).perspectiveEnabled = true;
          }

          // Assign controls directly and configure
          obj.controls = {
            tl: createPerspectiveControl('tl'),
            tr: createPerspectiveControl('tr'),
            bl: createPerspectiveControl('bl'),
            br: createPerspectiveControl('br'),
          };
          obj.set({
            hasControls: true,
            hasBorders: false,
            lockMovementX: true,
            lockMovementY: true,
            cornerSize: 16,
            padding: 0,
          });
          obj.setCoords();
          canvas.requestRenderAll();
        };

        const handlePerspectiveSelection = (options: any) => {
          const obj = options.target;
          if (obj) {
            setSelectedObject(obj);
            applyPerspectiveControls(obj);
          }
        };

        canvas.on('selection:created', handlePerspectiveSelection);
        canvas.on('selection:updated', handlePerspectiveSelection);
        canvas.on('selection:cleared', () => {
          setSelectedObject(null);
        });

        // Apply to already-selected object
        const activePerspObj = canvas.getActiveObject();
        if (activePerspObj) {
          handlePerspectiveSelection({ target: activePerspObj });
        }

        return () => {
          canvas.off('selection:created');
          canvas.off('selection:updated');
          canvas.off('selection:cleared');
        };

      case 'select':
        canvas.isDrawingMode = false;
        canvas.selection = true;
        canvas.defaultCursor = 'default';

        // Set cursor for all objects to move cursor and restore standard controls
        canvas.getObjects().forEach(obj => {
          obj.set({
            hoverCursor: 'move',
            lockMovementX: false,
            lockMovementY: false,
          });
          ensureConsistentCornerStyles(obj);
        });

        break;

      default:
        canvas.isDrawingMode = false;
        canvas.selection = true;
        canvas.defaultCursor = 'default';
    }

    return () => {
      canvas.off('mouse:down');
    };
  }, [selectedTool, canvas]);

  // Add drag and drop functionality for images
  useEffect(() => {
    if (!containerRef.current || !canvas || !canvasRef.current) return;

    const container = containerRef.current;
    const canvasElement = canvasRef.current;
    let dragCounter = 0; // Track drag enter/leave to handle nested elements

    // Prevent default browser behavior (opening files in new tab) at document level
    const preventDefaultDragOver = (e: DragEvent) => {
      if (e.dataTransfer && e.dataTransfer.types) {
        const hasFiles = Array.from(e.dataTransfer.types).some(type => 
          type === 'Files' || type === 'application/x-moz-file'
        );
        if (hasFiles) {
          e.preventDefault();
        }
      }
    };

    const preventDefaultDrop = (e: DragEvent) => {
      if (e.dataTransfer && e.dataTransfer.types) {
        const hasFiles = Array.from(e.dataTransfer.types).some(type => 
          type === 'Files' || type === 'application/x-moz-file'
        );
        if (hasFiles) {
          e.preventDefault();
        }
      }
    };

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter++;
      setIsDragging(true);
      container.classList.add('dragging');
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy';
      }
      setIsDragging(true);
      container.classList.add('dragging');
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter--;
      // Only reset if we've left the container completely
      if (dragCounter === 0) {
        setIsDragging(false);
        container.classList.remove('dragging');
      }
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter = 0;
      setIsDragging(false);
      container.classList.remove('dragging');

      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) {
        return;
      }

      // Process each dropped file
      Array.from(files).forEach((file) => {
        // Check if it's an image file
        if (!file.type.startsWith('image/')) {
          console.warn('Dropped file is not an image:', file.name, file.type);
          return;
        }

        // Use the existing handleImageUpload function
        handleImageUpload(file);
      });
    };

    // Prevent default browser behavior at document level
    document.addEventListener('dragover', preventDefaultDragOver);
    document.addEventListener('drop', preventDefaultDrop);

    // Attach events to both container and canvas element
    container.addEventListener('dragenter', handleDragEnter);
    container.addEventListener('dragover', handleDragOver);
    container.addEventListener('dragleave', handleDragLeave);
    container.addEventListener('drop', handleDrop);
    
    canvasElement.addEventListener('dragenter', handleDragEnter);
    canvasElement.addEventListener('dragover', handleDragOver);
    canvasElement.addEventListener('dragleave', handleDragLeave);
    canvasElement.addEventListener('drop', handleDrop);

    return () => {
      // Remove document-level handlers
      document.removeEventListener('dragover', preventDefaultDragOver);
      document.removeEventListener('drop', preventDefaultDrop);

      // Remove container handlers
      container.removeEventListener('dragenter', handleDragEnter);
      container.removeEventListener('dragover', handleDragOver);
      container.removeEventListener('dragleave', handleDragLeave);
      container.removeEventListener('drop', handleDrop);
      
      // Remove canvas element handlers
      canvasElement.removeEventListener('dragenter', handleDragEnter);
      canvasElement.removeEventListener('dragover', handleDragOver);
      canvasElement.removeEventListener('dragleave', handleDragLeave);
      canvasElement.removeEventListener('drop', handleDrop);
    };
  }, [canvas, handleImageUpload]);

  return (
    <ArtboardContainer>
      <ArtboardLabel id="artboard-label" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-200">
        <ArtboardIcon id="artboard-icon">
          <FaVectorSquare size={14} />
        </ArtboardIcon>
        <DimensionInput
          id="dimension-input-width"
          type="number"
          value={localWidth}
          onChange={handleWidthChange}
          onBlur={handleWidthBlur}
          onKeyPress={handleKeyPress}
          min="100"
          max="5000"
        />
        <Separator>×</Separator>
        <DimensionInput
          id="dimension-input-height"
          type="number"
          value={localHeight}
          onChange={handleHeightChange}
          onBlur={handleHeightBlur}
          onKeyPress={handleKeyPress}
          min="100"
          max="5000"
        />
        <span className='relative'>
          <ColorButton
            id="color-button-background"
            title="Background Color"
            className='btn btn-outline btn-sm p-0 w-7 h-7'
            onClick={() => colorInputRef.current?.click()}
          >
            <ColorSwatch $color={artboardBackgroundColor} />
          </ColorButton>
          <ColorPickerInput
            id="color-picker-input-background"
            className='absolute bottom-0 left-0 w-0 h-0 opacity-0'
            ref={colorInputRef}
            type="color"
            value={artboardBackgroundColor}
            onChange={handleColorChange}
          />
        </span>
        <PreviewButton
          id="preview-button-generate"
          className='btn btn-primary flex btn-xs'
          onClick={generatePreview}
          disabled={isGeneratingPreview}
          title="Preview with Demo Data"
        >
          <span className='py-1'><FaEye size={12} /></span>
          <span className='hidden xl:block'>{isGeneratingPreview ? 'Generating...' : 'Preview'}</span>
        </PreviewButton>
        <div style={{ position: 'relative' }}>
          <PreviewButton
            id="preview-button-copy-image-url"
            onClick={copyImageUrl}
            className='btn btn-primary flex btn-xs'
            title="Copy Image Code (only includes shortcodes used in canvas)"
          >
            <span className='py-1'><FaCopy size={12} /></span>
            <span className='hidden xl:block'>Image Code</span>
          </PreviewButton>
          {showCopiedMessage && (
            <CopiedMessage>
              Image code copied!
            </CopiedMessage>
          )}
        </div>
        <div style={{ position: 'relative' }}>
          <PreviewButton
            id="preview-button-copy-hubspot-url"
            onClick={copyHubSpotUrl}
            className='btn btn-primary flex btn-xs'
            title="Copy HubSpot Compatible Image Code (only includes shortcodes used in canvas)"
          >
            <span className='py-1'><FaCopy size={12} /></span>
            <span className='hidden xl:block'>HubSpot Code</span>
          </PreviewButton>
          {showHubSpotCopiedMessage && (
            <CopiedMessage>
              HubSpot code copied!
            </CopiedMessage>
          )}
        </div>
      </ArtboardLabel>
      <ArtboardWrapper id="artboard-wrapper" ref={containerRef}>
        <canvas ref={canvasRef} />
        {isObjectsLoading && (
          <div className="absolute inset-0 z-[100] flex items-center justify-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3">
              <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-gray-300 border-t-blue-500 dark:border-gray-600 dark:border-t-blue-400" />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Loading objects...</span>
            </div>
          </div>
        )}
        <DropOverlay id="drop-overlay" className={isDragging ? 'active' : ''}>
          <DropMessage>
            <DropIcon>📁</DropIcon>
            <div>
              <DropText>Drop image here</DropText>
              <DropSubtext>Release to add to canvas</DropSubtext>
            </div>
          </DropMessage>
        </DropOverlay>
      </ArtboardWrapper>
      <ObjectControls canvas={canvas} selectedObject={selectedObject} />
      
      {/* Preview Modal */}
      {showPreview && (
        <PreviewModal onClick={closePreview}>
          <PreviewContent onClick={(e) => e.stopPropagation()}>
            <PreviewImageContainer>
              {isGeneratingPreview || isImageLoading ? (
                <Preloader>
                  <Spinner />
                  <LoadingText>
                    {isGeneratingPreview ? 'Generating preview...' : 'Loading image...'}
                  </LoadingText>
                </Preloader>
              ) : imageLoadError ? (
                <Preloader>
                  <LoadingText style={{ color: '#e74c3c' }}>
                    Failed to load preview image. Please try again.
                  </LoadingText>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    <button
                      onClick={() => {
                        setImageLoadError(false);
                        setIsImageLoading(true);
                        // Force a re-render of the image
                        setPreviewImage('');
                        setTimeout(() => {
                          setPreviewImage(previewImage);
                        }, 100);
                      }}
                      style={{
                        background: '#4285f4',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Retry
                    </button>
                    <button
                      onClick={async () => {
                        // Generate local preview as fallback
                        if (canvas) {
                          const dataUrl = await generateImage();
                          if (dataUrl) {
                            setPreviewImage(dataUrl);
                            setImageLoadError(false);
                            setIsImageLoading(false);
                          }
                        }
                      }}
                      style={{
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Generate Local Preview
                    </button>
                  </div>
                </Preloader>
              ) : null}
              {previewImage && (
                <>
                  <PreviewImage 
                    src={previewImage} 
                    alt="Preview with demo data"
                    id="preview-image"
                    className=''
                    style={{ 
                      display: (isGeneratingPreview || isImageLoading) ? 'none' : 'block' 
                    }}
                    onLoad={() => {
                      setIsImageLoading(false);
                      setImageLoadError(false);
                    }}
                    onError={(e) => {
                      console.error('Failed to load preview image:', e);
                      e.currentTarget.style.display = 'none';
                      setIsImageLoading(false);
                      setImageLoadError(true);
                    }}
                  />
                  {!isGeneratingPreview && !isImageLoading && !imageLoadError && (
                    <>
                      <CloseButton onClick={closePreview}>&times;</CloseButton>
                      <ViewImageButton
                        onClick={() => {
                          if (previewImage) {
                            window.open(previewImage, '_blank');
                          }
                        }}
                        disabled={!previewImage}
                      >
                        <FaExternalLinkAlt size={12} /> View Full Image
                      </ViewImageButton>
                    </>
                  )}
                </>
              )}
            </PreviewImageContainer>
          </PreviewContent>
        </PreviewModal>
      )}
    </ArtboardContainer>
  );
};

export default Artboard; 